import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { GeoPoint, collection, doc, getDocs, query, where, deleteDoc, setDoc, Firestore, Timestamp } from '@angular/fire/firestore';
import {
  getDownloadURL,
  ref,
  Storage,
  uploadBytesResumable,
} from '@angular/fire/storage';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormArray,
  FormBuilder,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { ActivatedRoute } from '@angular/router';
import { select, Store } from '@ngrx/store';
import {
  UppyAngularDashboardModalModule,
  UppyAngularDashboardModule,
  UppyAngularDragDropModule,
  UppyAngularProgressBarModule,
  UppyAngularStatusBarModule,
} from '@uppy/angular';
import Uppy from '@uppy/core';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { EventStatus } from '../../../../models/event.model';
import { Group } from '../../../../models/group.model';
import { FireTimestamp } from '../../../../models/timestamp.firestore.model';
import { AuthService } from '../../../../services/auth.service';
import * as eventActions from '../../../../state/actions/event.actions';
import { AppState } from '../../../../state/appstate';
import * as eventSelector from '../../../../state/selectors/event.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { InitialsPipe } from '../../../../pipes/initials.pipe';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnSubmitComponent } from '../../../components/buttons/btn-submit/btn-submit.component';
import { AddLocationComponent } from '../../../components/maps/add-location/add-location.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
export class EventTimeValidator {
  static validate(): ValidatorFn {
    return (controls: AbstractControl) => {
      const startDate = moment(controls.get('startDate')?.value);
      const endDate = moment(controls.get('endDate')?.value);
      const startTime = controls.get('startTime')?.value;
      const endTime = controls.get('endTime')?.value;

      const startTimeValue = moment(startTime, 'HH:mm');
      const endTimeValue = moment(endTime, 'HH:mm');

      const startDateTime = moment(startDate).set({
        hour: startTimeValue.hours(),
        minute: startTimeValue.minutes(),
      });
      const endDateTime = moment(endDate).set({
        hour: endTimeValue.hours(),
        minute: endTimeValue.minutes(),
      });

      const endDateControl = controls.get('endDate');
      const endTimeControl = controls.get('endTime');

      if (startDate.isAfter(endDate)) {
        endDateControl?.setErrors({ 'invalid-end-date': true });
        return { 'invalid-end-date': true };
      }

      if (
        startDateTime.isAfter(endDateTime) ||
        startDateTime.isSame(endDateTime)
      ) {
        endTimeControl?.setErrors({ 'invalid-end-time': true });
        return { 'invalid-end-time': true };
      }

      // Clear previous errors if validation passed
      if (endDateControl?.errors && endDateControl.errors['invalid-end-date']) {
        delete endDateControl.errors['invalid-end-date'];
        if (!Object.keys(endDateControl.errors).length) {
          endDateControl.setErrors(null);
        }
      }

      if (endTimeControl?.errors && endTimeControl.errors['invalid-end-time']) {
        delete endTimeControl.errors['invalid-end-time'];
        if (!Object.keys(endTimeControl.errors).length) {
          endTimeControl.setErrors(null);
        }
      }
      return null;
    };
  }
}

@Component({
  selector: 'app-create-event',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    UppyAngularDashboardModule,
    UppyAngularDashboardModalModule,
    UppyAngularDragDropModule,
    UppyAngularProgressBarModule,
    UppyAngularStatusBarModule,
    BtnBackComponent,
    BtnSubmitComponent,
    AddLocationComponent,
    GoogleMapsModule,
    InitialsPipe,
    BtnFabComponent,
  ],
  templateUrl: './create-event.component.html',
  styleUrls: ['./create-event.component.css'],
})
export class CreateEventComponent implements OnInit, OnDestroy {
  pageForm: FormGroup;

  route: ActivatedRoute = inject(ActivatedRoute);
  private readonly afs = inject(Firestore);
  private readonly fb = inject(FormBuilder);

  private readonly storage: Storage = inject(Storage);

  groupId: string | null = this.route.snapshot.paramMap.get('id');
  eventId: string | null = this.route.snapshot.queryParamMap.get('eventId');
  group: Group | null | undefined;
  groupSub: Subscription | undefined;
  eventSub: Subscription | undefined;

  moment = moment;
  uuid = uuidv4();

  progress: number = 0;
  message: string = '';

  uppy: Uppy = new Uppy({
    autoProceed: false,
    restrictions: {
      maxFileSize: 5242880, // 5 MB
      maxNumberOfFiles: 1,
      minNumberOfFiles: 1,
      allowedFileTypes: ['.jpg', '.jpeg', '.png'],
    },
  });

  dashboardProps = {
    inline: true,
    hideUploadButton: true,
    showSelectedFiles: true,
    proudlyDisplayPoweredByUppy: false,
    width: '100%',
    height: '130px',
    note: 'Images only (jpg, jpeg, png), up to 5 MB. Only one image is allowed.',
  };

  working: boolean = false;
  workingSub: Subscription | undefined;

  constructor(
    private store: Store<AppState>,
    private auth: AuthService,
    private toastr: ToastrService
  ) {
    this.pageForm = new FormGroup(
      {
        name: new FormControl('', [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(50),
        ]),
        description: new FormControl('', [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(500),
        ]),
        startDate: new FormControl(
          moment().format('YYYY-MM-DD'),
          Validators.required
        ),
        endDate: new FormControl(
          moment().format('YYYY-MM-DD'),
          Validators.required
        ),
        startTime: new FormControl(
          moment().format('HH:mm'),
          Validators.required
        ),
        endTime: new FormControl(
          moment().add(1, 'hour').format('HH:mm'),
          Validators.required
        ),
        locationName: new FormControl(''),
        position: new FormControl(''),
        isPrivate: new FormControl(false),
        events: this.fb.array([]),
      },
      {
        validators: EventTimeValidator.validate(),
      }
    );
  }

  get eventsArray(): FormArray {
    return this.pageForm.get('events') as FormArray;
  }

  addEventRow(name = '', date: string | null = null, id = '', requestVolunteers = false, volunteersRequired = 0, volunteerCriteria = '') {
    const uniqueId =
      id || `event_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    const eventGroup = this.fb.group({
      id: [uniqueId],
      name: [name, [Validators.required, Validators.maxLength(100)]],
      date: [date, [Validators.required]],
      requestVolunteers: [requestVolunteers],
      volunteersRequired: [volunteersRequired],
      volunteerCriteria: [volunteerCriteria],
    });
    this.eventsArray.push(eventGroup);
  }

  removeEventRow(index: number) {
    this.eventsArray.removeAt(index);
  }

  ngOnInit(): void {
    this.groupSub = this.store
      .pipe(select(groupSelector.getGroupById(this.groupId!)))
      .subscribe((group) => {
        this.group = group;
        if (this.group?.location?.position) {
          this.pageForm.patchValue({
            position: this.group?.location?.position,
          });
        }
      });

    this.workingSub = this.store
      .select((state) => state.events)
      .pipe(select(eventSelector.inProgress))
      .subscribe((inProgress) => {
        this.working = inProgress;
      });

    console.log(
      `CreateEventComponent: ngOnInit: this.eventId: ${this.eventId}`
    );
    if (this.eventId) {
      this.eventSub = this.store
        .select(eventSelector.getEventById(this.eventId))
        .subscribe((event) => {
          if (event) {
            this.pageForm.patchValue({
              name: event.name,
              description: event.description,
              startDate: moment(event.startDateTime.toDate())
                .add(1, 'week')
                .format('YYYY-MM-DD'),
              endDate: moment(event.endDateTime.toDate())
                .add(1, 'week')
                .format('YYYY-MM-DD'),
              startTime: moment(event.startDateTime.toDate()).format('HH:mm'),
              endTime: moment(event.endDateTime.toDate()).format('HH:mm'),
              locationName: event.location?.name,
              position: event.location?.position,
              isPrivate: event.isPrivate,
            });

            // Clear existing events array
            while (this.eventsArray.length) {
              this.eventsArray.removeAt(0);
            }

            if (event?.events) {
              Object.entries(event.events).forEach(
                ([key, val]: [string, any]) => {
                  const dateVal = val.date
                    ? val.date.toDate ? val.date.toDate() : val.date
                    : null;
                  const shiftedDateStr = dateVal
                    ? moment(dateVal).add(1, 'week').format('YYYY-MM-DDTHH:mm')
                    : null;
                  this.addEventRow(val.name, shiftedDateStr, '', val.requestVolunteers, val.volunteersRequired, val.volunteerCriteria);
                },
              );
            }
          }
          console.log(`CreateEventComponent: ngOnInit: event: ${event}`);
        });
    }
  }

  ngOnDestroy(): void {
    this.groupSub?.unsubscribe();
    this.workingSub?.unsubscribe();
    this.eventSub?.unsubscribe();
  }

  setPosition(position: GeoPoint | null) {
    this.pageForm.patchValue({ position: position });
  }

  createEvent() {
    if (this.pageForm.invalid || !this.group) return;

    const file: any = this.uppy.getFiles()[0];

    const uid = this.auth.getUid();

    if (file != null && uid != null) {
      this.working = true;

      const storageRef = ref(
        this.storage,
        `eventFlyers/${this.groupId}/${this.uuid}`
      );

      const metadata = {
        contentType: file.type,
        customMetadata: {
          uid: uid!,
        },
      };

      const uploadTask = uploadBytesResumable(storageRef, file.data, metadata);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          this.message = `${progress.toFixed(2)}%`;
        },
        (error) => {
          this.toastr.error(
            'Event image could not be updated. Please try again later.',
            'Image upload Failed'
          );
          this.working = false;
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            this.createDatabaseEntry(this.uuid, downloadURL);
          });
        }
      );
    } else {
      // Create Event without Image
      this.createDatabaseEntry(this.uuid);
    }
  }

  createDatabaseEntry(id: string, url?: string | undefined) {
    const eventsMap: Record<string, { date: Timestamp; name: string; requestVolunteers: boolean; volunteersRequired: number; volunteerCriteria: string }> = {};
    this.eventsArray.controls.forEach((control) => {
      const g = control as FormGroup;
      const eventId = g.get('id')?.value;
      const dateVal = g.get('date')?.value;
      const nameVal = g.get('name')?.value;
      const requestVolunteersVal = g.get('requestVolunteers')?.value || false;
      const volunteersRequiredVal = g.get('volunteersRequired')?.value || 0;
      const volunteerCriteriaVal = g.get('volunteerCriteria')?.value || '';
      if (eventId && dateVal) {
        eventsMap[eventId] = {
          date: Timestamp.fromDate(moment(dateVal).toDate()),
          name: nameVal || '',
          requestVolunteers: requestVolunteersVal,
          volunteersRequired: requestVolunteersVal ? volunteersRequiredVal : 0,
          volunteerCriteria: requestVolunteersVal ? volunteerCriteriaVal : '',
        };
      }
    });

    let data: any = {
      groupId: this.group?.id!,
      groupName: this.group?.name!,
      name: this.pageForm.get('name')?.value,
      description: this.pageForm.get('description')?.value,
      isPrivate: this.pageForm.get('isPrivate')?.value,
      status: EventStatus.active,
      events: Object.keys(eventsMap).length > 0 ? eventsMap : null,
    };

    const startTimeValue = moment(
      this.pageForm.get('startTime')?.value,
      'HH:mm'
    );
    const endTimeValue = moment(this.pageForm.get('endTime')?.value, 'HH:mm');

    data.startDateTime = FireTimestamp.fromDate(
      moment(this.pageForm.get('startDate')?.value)
        .set({
          hour: startTimeValue.hours(),
          minute: startTimeValue.minutes(),
        })
        .toDate()
    );
    data.endDateTime = FireTimestamp.fromDate(
      moment(this.pageForm.get('endDate')?.value)
        .set({
          hour: endTimeValue.hours(),
          minute: endTimeValue.minutes(),
        })
        .toDate()
    );

    data.bannerImage = url ?? this.group?.imageUrl;

    if (this.pageForm.get('position')?.value) {
      data.location = {
        id: this.uuid,
        position: this.pageForm.get('position')?.value,
        name:
          this.pageForm.get('locationName')?.value != ''
            ? this.pageForm.get('locationName')?.value
            : data.name,
      };
    }

    // Sync to events_calendar subcollection in the background
    const calendarCollRef = collection(
      this.afs,
      'groups',
      this.groupId!,
      'events_calendar',
    );
    const q = query(calendarCollRef, where('refId', '==', id));

    getDocs(q)
      .then(async (snap) => {
        // Map from subEventId to existing volunteers array
        const existingVolunteersMap: Record<string, any[]> = {};
        snap.docs.forEach((docSnap) => {
          const calendarData = docSnap.data();
          const docId = docSnap.id;
          const parts = docId.split('_');
          const subEventId = parts[parts.length - 1];
          if (subEventId) {
            existingVolunteersMap[subEventId] = calendarData['volunteers'] || [];
          }
        });

        // 1. Delete existing calendar entries for this event
        const deletePromises = snap.docs.map((docSnap) =>
          deleteDoc(docSnap.ref),
        );
        await Promise.all(deletePromises);

        // 2. Re-create new calendar entries from events map
        if (data.events) {
          const createPromises = Object.entries(data.events).map(
            ([subEventId, subEvent]: [string, any]) => {
              const subEventDate = subEvent.date;
              if (!subEventDate) return Promise.resolve();

              const eventMoment = moment(
                subEventDate.toDate ? subEventDate.toDate() : subEventDate,
              );
              const calendarDocRef = doc(
                this.afs,
                'groups',
                this.groupId!,
                'events_calendar',
                `event_${id}_${subEventId}`,
              );

              const vols = existingVolunteersMap[subEventId] || [];

              return setDoc(
                calendarDocRef,
                {
                  groupId: this.groupId,
                  type: 'event', // type is event
                  name: subEvent.name || data.name,
                  description: data.description,
                  date: subEventDate,
                  eventDate: subEventDate,
                  month: eventMoment.month() + 1,
                  year: eventMoment.year(),
                  day: eventMoment.date(),
                  refId: id,
                  imageUrl: url || data.bannerImage || null,
                  volunteersRequired: subEvent.requestVolunteers ? subEvent.volunteersRequired || 0 : 0,
                  volunteerCriteria: subEvent.requestVolunteers ? subEvent.volunteerCriteria || '' : '',
                  volunteers: vols,
                },
                { merge: true },
              );
            },
          );
          await Promise.all(createPromises);
        }
      })
      .catch((err) => {
        console.error('Failed to sync to events_calendar:', err);
      });

    this.store.dispatch(eventActions.create({ id: id, event: data }));
  }
}
